import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { createWebSocketConnection } from '../services/WebSocketService';
import type { WebSocketService } from '../services/WebSocketService';
import IdleNotificationPopup from '../components/IdleNotificationPopup';
import CostAlertPopup from '../components/CostAlertPopup';
import type { CostAlert } from '../components/CostAlertPopup';

export interface IdleWarning {
  instance_id: string;
  instance_name: string;
  seconds_remaining: number;
  urgent?: boolean;
}

export interface BalanceWarning {
  balance: number;
  estimated_minutes: number;
}

interface IdleNotificationContextType {
  idleWarnings: IdleWarning[];
  dismissWarning: (instanceId: string) => void;
  balanceWarning: BalanceWarning | null;
  dismissBalanceWarning: () => void;
  costAlerts: CostAlert[];
  dismissCostAlert: (alertKind: string) => void;
}

const IdleNotificationContext = createContext<IdleNotificationContextType | null>(null);

export function useIdleNotification() {
  const context = useContext(IdleNotificationContext);
  if (!context) {
    return {
      idleWarnings: [] as IdleWarning[],
      dismissWarning: () => {},
      balanceWarning: null as BalanceWarning | null,
      dismissBalanceWarning: () => {},
      costAlerts: [] as CostAlert[],
      dismissCostAlert: () => {},
    };
  }
  return context;
}

export function IdleNotificationProvider({ children }: { children: React.ReactNode }) {
  const [idleWarnings, setIdleWarnings] = useState<IdleWarning[]>([]);
  const [balanceWarning, setBalanceWarning] = useState<BalanceWarning | null>(null);
  const [costAlerts, setCostAlerts] = useState<CostAlert[]>([]);
  const wsRef = useRef<WebSocketService | null>(null);

  const dismissWarning = useCallback((instanceId: string) => {
    setIdleWarnings(prev => prev.filter(w => w.instance_id !== instanceId));
  }, []);

  const dismissBalanceWarning = useCallback(() => {
    setBalanceWarning(null);
  }, []);

  const dismissCostAlert = useCallback((alertKind: string) => {
    setCostAlerts(prev => prev.filter(a => a.alert_kind !== alertKind));
  }, []);

  const handleMessage = useCallback((msg: { type: string; instance_id?: string; instance_name?: string; seconds_remaining?: number; balance?: number; estimated_minutes?: number; alert_kind?: string; threshold?: number; current_value?: number; message?: string }) => {
    if (msg.type === 'balance_warning') {
      setBalanceWarning({
        balance: msg.balance ?? 0,
        estimated_minutes: msg.estimated_minutes ?? 0,
      });
      return;
    }
    if (msg.type === 'cost_alert' && msg.alert_kind) {
      const alert: CostAlert = {
        alert_kind: msg.alert_kind,
        threshold: msg.threshold ?? 0,
        current_value: msg.current_value ?? 0,
        message: msg.message ?? '',
      };
      setCostAlerts(prev => {
        const filtered = prev.filter(a => a.alert_kind !== alert.alert_kind);
        return [...filtered, alert];
      });
      return;
    }
    if (msg.type === 'idle_warning' && msg.instance_id && msg.instance_name) {
      setIdleWarnings(prev => {
        const filtered = prev.filter(w => w.instance_id !== msg.instance_id);
        return [...filtered, {
          instance_id: msg.instance_id!,
          instance_name: msg.instance_name!,
          seconds_remaining: msg.seconds_remaining ?? 300,
        }];
      });
    } else if (msg.type === 'idle_confirm' && msg.instance_id && msg.instance_name) {
      setIdleWarnings(prev => {
        const filtered = prev.filter(w => w.instance_id !== msg.instance_id);
        return [...filtered, {
          instance_id: msg.instance_id!,
          instance_name: msg.instance_name!,
          seconds_remaining: msg.seconds_remaining ?? 30,
          urgent: true,
        }];
      });
    } else if (msg.type === 'idle_terminated' && msg.instance_id) {
      setIdleWarnings(prev => prev.filter(w => w.instance_id !== msg.instance_id));
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    let userId: string;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      userId = payload.sub || payload.user_id;
      if (!userId) return;
    } catch {
      return;
    }

    const ws = createWebSocketConnection(`/ws/launch-status/${userId}`, {
      reconnect: true,
    });
    ws.onMessage(handleMessage);
    ws.connectAsync();
    wsRef.current = ws;

    return () => {
      ws.close();
      wsRef.current = null;
    };
  }, [handleMessage]);

  return (
    <IdleNotificationContext.Provider value={{ idleWarnings, dismissWarning, balanceWarning, dismissBalanceWarning, costAlerts, dismissCostAlert }}>
      {children}
      <IdleNotificationPopup warnings={idleWarnings} onDismiss={dismissWarning} />
      <CostAlertPopup alerts={costAlerts} onDismiss={dismissCostAlert} />
    </IdleNotificationContext.Provider>
  );
}
